import {
	FormItem,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { Builder, BuilderHandle } from "../../Builder/Builder";
import { HubItemComposerDialog } from "../HubItemComposerDialog/HubItemComposerDialog";
import { BuilderTitleInput } from "../HubItemComposerDialog/BuilderTitleInput";
import {
	ReactElement,
	Ref,
	RefObject,
	forwardRef,
	useCallback,
	useImperativeHandle,
	useRef
} from "react";
import { HubMediaSelect } from "../../HubMediaSelect/HubMediaSelect";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { createValidationConfiguration } from "client/Validation";
import { doesNotExceedLength } from "client/Validation/rules";
import { ExistingHubItemImages } from "client/Api/Hub/common.composer";
import { ensureNotNil } from "client/Misc/utils";
import { HubTemplateSaveModel } from "client/Api/Hub/Templates/hubTemplateManagement";
import { HubItemLayoutFormModel, HubItemMediaFormModel } from "../common";
import { HubItemImageRestrictionsModel } from "client/Api/Hub/common";
import { DocumentLocationViewModel } from "client/Api/Hub/hubSettings";
const {
	ToolbarContainer,
	Content,
	BuilderContainer,
	BuilderLayoutContainer
} = HubItemComposerDialog;

export type HubTemplateFormContentProps = {
	attachable: IValueInputAttachable<FormData>;
	imageRestrictions: HubItemImageRestrictionsModel;
	defaultImageLocation: DocumentLocationViewModel | null;
	hubItemKind: HubItemKind;
	originalImages: ExistingHubItemImages;
	isPixabayImageGalleryAvailable: boolean;
};

export type HubTemplateFormContentHandle = {
	getIsContentChanged(): boolean;
};

export const HubTemplateFormContent = forwardRef(
	(props: HubTemplateFormContentProps, ref: Ref<HubTemplateFormContentHandle>): ReactElement => {
		const {
			attachable,
			imageRestrictions,
			defaultImageLocation,
			hubItemKind,
			originalImages,
			isPixabayImageGalleryAvailable
		} = props;

		const builderRef: RefObject<BuilderHandle | null> = useRef<BuilderHandle>(null);

		const disableToolbarHandler = useCallback(
			(): void => {
				builderRef.current
					?.hideToolbar();
			},
			[]
		);

		useImperativeHandle(
			ref,
			() => ({
				getIsContentChanged(): boolean {
					return ensureNotNil(builderRef.current)
						.getIsCurrentEditorChanged();
				},
			}),
			[]
		);

		return (
			<Builder
				ref={builderRef}
				imageRestrictions={imageRestrictions}
				defaultImageLocation={defaultImageLocation}
				isPixabayImageGalleryAvailable={isPixabayImageGalleryAvailable}
			>
				<ToolbarContainer isToolbarVisible>
					<Builder.Toolbar />
				</ToolbarContainer>
				<Content hasNoPadding>
					<BuilderContainer>
						<FormItem
							isRequired
						>
							<BuilderTitleInput
								attachable={attachable.for("title")}
								hubItemKind={hubItemKind}
								shouldFocusTitle
								onFocus={disableToolbarHandler}
							/>
						</FormItem>
						<FormItem>
							<HubMediaSelect
								hubItemKind={hubItemKind}
								attachable={attachable.for("media")}
								imageRestrictions={imageRestrictions}
								defaultImageLocation={defaultImageLocation}
								originalImageMedia={originalImages.coverImage}
								isDisabled={false}
								onDisableToolbar={disableToolbarHandler}
								isPixabayImageGalleryAvailable={isPixabayImageGalleryAvailable}
							/>
						</FormItem>
						<FormItem isForGroup>
							<BuilderLayoutContainer>
								<Builder.Layout
									hubItemKind={hubItemKind}
									attachable={attachable.for("layout")}
									originalImages={originalImages.layoutImages}
								/>
							</BuilderLayoutContainer>
							<ValidationError
								{...attachable.for("layout")
									.attachToValidationError()
								}
							/>
						</FormItem>
					</BuilderContainer>
				</Content>
			</Builder>
		);
	}
);
HubTemplateFormContent.displayName = "HubTemplateFormContent";

export type FormData = OverrideProperties<
	HubTemplateSaveModel,
	{
		media: HubItemMediaFormModel;
		layout: HubItemLayoutFormModel;
	}
>;

export const hubTemplateFormValidationConfiguration = createValidationConfiguration<FormData>();

hubTemplateFormValidationConfiguration.forScalar("title")
	.add(
		doesNotExceedLength(255)
	);
